import { useContext, useEffect, useState } from 'react';
import ImageInput from './ImageInput';
import CrossGray from '../../../../../Assets/StorefrontIcons/CrossGray.svg';
import { useDispatch, useSelector } from 'react-redux';
import { addDesktopItemToLast, pushToVersions, setSectionFormType, updateVersionIndex } from '../../../../../Features/storefrontSlice';
import Links from './Links';
import { StorefrontContext } from '../../../../../Context/StorefrontContext';
import { v4 as uuidv4 } from 'uuid';
import imageCompression from 'browser-image-compression';
import { getMediaUrl } from '../../../../../lib/utils/utils';
import { getCategory, getCollection, getCustomCollection, getProduct } from '../../../../../lib/utils/apiCalls';

function SingleBannerDesktopForm() {
    const dispatch = useDispatch();

    const {
        singleBannerDesktop,
        defaultSingleBanner,
        setSingleBannerDesktop,
    } = useContext(StorefrontContext);

    const versionStack = useSelector((state) => state.storefront.versionStack);
    const versionIndex = useSelector((state) => state.storefront.versionIndex);

    const [altText, setAltText] = useState('');

    const defaultFormErrors = {
        altText: '',
        link: '',
        image: ''
    }

    const [formErrors, setFormErrors] = useState(defaultFormErrors);

    function closeForm() {
        dispatch(setSectionFormType(null));
        setSingleBannerDesktop(defaultSingleBanner);
    }

    function handleChange(e) {
        setAltText(e.target.value);
    }

    async function handleSave() {
        const currentVersion = versionStack[versionIndex];

        const validationErrors = {};

        if(!singleBannerDesktop.image.file) {
            validationErrors.image = 'Image required';
        }

        if(!altText) {
            validationErrors.altText = 'Required field';
        }

        const areAllKeysEmpty = Object.entries(singleBannerDesktop)
        .filter(([key]) => key !== 'image')
        .every(([, value]) => !value || Object.keys(value).length === 0);
    
        if (areAllKeysEmpty) {
            validationErrors.link = 'Link is required';
        }

        if(Object.keys(validationErrors).length === 0) {
            const object = {
                typeData: 'SINGLE_BANNER',
                altText,
            };

            if(Object.keys(singleBannerDesktop.category).length > 0) {
                object.typeProduct = 'CATEGORY';
                object.source = singleBannerDesktop.category.id;
            }
            else if(Object.keys(singleBannerDesktop.categoryCollection).length > 0) {
                object.typeProduct = 'COLLECTION';
                object.source = singleBannerDesktop.categoryCollection.id;
            }
            else if(Object.keys(singleBannerDesktop.customCollection).length > 0) {
                object.typeProduct = 'CUSTOM_COLLECTION';
                object.source = singleBannerDesktop.customCollection.id;
            }
            else if(Object.keys(singleBannerDesktop.product).length > 0) {
                object.typeProduct = 'PRODUCT';
                object.source = singleBannerDesktop.product.id || singleBannerDesktop.product.product.id;
            }

            const image = singleBannerDesktop.image.file;

            if (image) {
                let url;

                if (image.size > 1048576) {
                    try {
                        const compressedBlob = await imageCompression(image, { maxSizeMB: 1, maxWidthOrHeight: 1920, useWebWorker: true });
                        const compressedFile = new File([compressedBlob], image.name, { type: image.type, lastModified: Date.now() });

                        url = await getMediaUrl(compressedFile);
                    }
                    catch (err) {
                        console.warn('Image compression failed, using original file:', err);
                        url = await getMediaUrl(image);
                    }
                }
                else {
                    url = await getMediaUrl(image);
                }
                object.imageLink = url;
            }
            else {
                object.imageLink = singleBannerDesktop.image.url || "";
            }

            const tempVersionData = [ ...currentVersion.versionData ];
            const tempWebVersionData = [ ...currentVersion.webVersionData ];

            const singleBannerDesktopDataIndex = tempVersionData.findIndex(
                item => item.item.typeData === 'SINGLE_BANNER'
            );

            if (singleBannerDesktopDataIndex !== -1) {
                tempWebVersionData[singleBannerDesktopDataIndex] = {
                    ...tempWebVersionData[singleBannerDesktopDataIndex],
                    item: object,
                };

                tempVersionData[singleBannerDesktopDataIndex] = {
                    ...tempVersionData[singleBannerDesktopDataIndex],
                    item: object,
                };

                const newIndex = versionStack[versionStack.length - 1].index + 1;

                const newVersion = {
                    ...currentVersion,
                    index: newIndex,
                    versionData: tempVersionData,
                    webVersionData: tempWebVersionData,
                    comment: 'Update desktop single banner section',
                }

                dispatch(pushToVersions(newVersion));
                dispatch(updateVersionIndex(newIndex));
            }
            else {
                const newObject = {
                    id: uuidv4(),
                    ...object
                }

                dispatch(addDesktopItemToLast(newObject));
            }
            closeForm();
        }
        else {
            setFormErrors(validationErrors);
        }
    }

    useEffect(() => {
        async function setSingleBannerDesktopData() {
            const tempVersion = versionStack[versionIndex]?.webVersionData;

            if (tempVersion && tempVersion.length > 0) {
                const foundItem = tempVersion.find(item => item.item.typeData === 'SINGLE_BANNER');

                if(foundItem) {
                    const { item } = foundItem;

                    setAltText(item.altText);

                    let object = { ...defaultSingleBanner };

                    try {
                        if (item.typeProduct === 'CATEGORY_COLLECTION') {
                            const response = await getCollection(item.source);
                            if (response.status === 200) {
                                object = { ...defaultSingleBanner, categoryCollection: response.data.data.collection };
                            }
                        }
                        else if (item.typeProduct === 'CATEGORY') {
                            const response = await getCategory(item.source);
                            if (response.status === 200) {
                                object = { ...defaultSingleBanner, category: response.data.data.category };
                            }
                        }
                        else if (item.typeProduct === 'CUSTOM_COLLECTION') {
                            const response = await getCustomCollection(item.source);
                            if (response.status === 200) {
                                object = { ...defaultSingleBanner, customCollection: response.data.data.collection };
                            }
                        }
                        else if (item.typeProduct === 'PRODUCT') {
                            const response = await getProduct(item.source);

                            if (response.status === 200) {
                                object = { ...defaultSingleBanner, product: response.data.data };
                            }
                        }
                        else {
                            object = { ...defaultSingleBanner };
                        }
                    }
                    catch (err) {
                        console.log(err);
                    }

                    object.image = {
                        id: uuidv4(),
                        url: item.imageLink,
                        file: null,
                    };

                    setSingleBannerDesktop(object);
                }
            }
        }

        setSingleBannerDesktopData();
        // eslint-disable-next-line
    }, [versionStack, versionIndex]);

    return (
        <div className='w-[305px] h-full bg-[#FFFFFF] border-l border-[#EAEAEA] flex flex-col gap-4 select-none relative'>
            <div className="flex items-center justify-between">
                <p className='font-semibold text-[20px] leading-6 text-[#121212] pl-3'>Single Banner</p>

                <div className="size-11 p-[10px] flex items-center justify-center">
                    <button onClick={closeForm} className='size-6 flex items-center justify-center outline-none'>
                        <img src={CrossGray} alt="" />
                    </button>
                </div>
            </div>

            <div className="h-[calc(100%-44px)] flex flex-col gap-3 px-3 overflow-y-scroll no-scrollbar pb-16">
                <ImageInput 
                    errorText={formErrors.image} 
                />

                <div className="flex flex-col gap-2">
                    <div className="flex items-center justify-between">
                        <label className='font-medium text-[14px] leading-6 text-[#121212] cursor-pointer' htmlFor="alt-text">Alt Text</label>

                        <p className='text-[12px] leading-5 text-[#EF4444]'>{formErrors.altText}</p>
                    </div>

                    <textarea 
                        className='outline-none h-24 border border-[#EAEAEA] bg-[#F5F5F5] p-3 rounded-[9px] font-normal text-[12px] leading-6 text-[#121212] placeholder:text-[#666666]' 
                        name="alt-text" 
                        id="alt-text" 
                        placeholder='Describe your image for people with visual impairments' 
                        value={altText} 
                        onChange={handleChange} 
                    />
                </div>

                <Links 
                    errorText={formErrors.link} 
                />
            </div>

            <div className="absolute bottom-0 left-0 w-full p-3 bg-[#FFFFFF]">
                <button onClick={handleSave} className='w-full outline-none px-4 py-[6px] bg-primaryColor hover:bg-primaryAccent rounded-md font-semibold text-[12px] leading-6 text-buttonText'>Save changes</button>
            </div>
        </div>
    );
}

export default SingleBannerDesktopForm;
